import { Button, cn, ScrollShadow } from '@heroui/react'
import { File, X } from 'lucide-react'
import { FileUploader } from 'react-drag-drop-files'
import {
  PiFileAudio,
  PiFileCsv,
  PiFileDoc,
  PiFilePdf,
  PiFileText,
  PiFileVideo,
} from 'react-icons/pi'
import { toast } from 'react-toastify'

function FileCard({ file, handleRemove }) {
  const formatFileSize = (bytes) => {
    if (!bytes)
      return 'Unknown size'
    if (bytes < 1024)
      return `${bytes} bytes`
    if (bytes < 1024 * 1024)
      return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / 1024 / 1024).toFixed(2)} MB`
  }

  return (
    <div
      className="bg-white rounded-md border w-full border-gray-200 p-2 flex gap-2.5 items-center justify-between"
    >
      <div className="flex gap-2 items-center min-w-0">
        <File size={24} className="shrink-0 text-gray-400" />
        <div className="flex flex-col min-w-0">
          <span className="font-medium text-xs text-gray-900 truncate">
            {file.name}
          </span>
          <div className="flex gap-1.5 text-2xs text-gray-500">
            <span className="truncate">
              {file.type || 'Unknown type'}
            </span>
            <span className="shrink-0">•</span>
            <span className="shrink-0">
              {file.size
                ? formatFileSize(file.size)
                : 'Unknown size'}
            </span>
          </div>
        </div>
      </div>
      <Button
        variant="ghost"
        isIconOnly
        color="gray"
        size="sm"
        onPress={handleRemove}
      >
        <X size={16} />
      </Button>
    </div>
  )
}

export default function FileUpload(
  {
    fileOrFiles,
    setFileOrFiles,
    multiple,
    types,
    name,
    label,
    description,
    className,
    maxFiles,
    ...props
  },
) {
  const getReadableFormats = () => {
    if (!types || !Array.isArray(types))
      return ''

    const formatMap = {
      PDF: 'PDF',
      MP3: 'MP3',
      MP4: 'MP4',
      TXT: 'TXT',
      CSV: 'CSV',
      DOC: 'DOC',
      DOCX: 'DOCX',
      MPEG: 'MPEG',
      M4A: 'M4A',
    }

    return types
      .map(type => formatMap[type.toUpperCase()] || type)
      .filter((value, index, self) => self.indexOf(value) === index)
      .join(', ')
  }

  const isAudioOnly = types && types.every(type =>
    ['MP3', 'M4A', 'MPEG', 'WAV', 'FLAC'].includes(type.toUpperCase()),
  )

  const isCsvOnly = types && types.length === 1 && types[0].toUpperCase() === 'CSV'

  function handleAdd(fileList) {
    if (multiple) {
      const newFiles = Array.from(fileList)
      if (newFiles.length + fileOrFiles.length > maxFiles) {
        toast.error(`You can only upload ${maxFiles} files`)
        return
      }
      setFileOrFiles(prev => [...prev, ...newFiles])
    }
    else {
      setFileOrFiles(fileList)
    }
  }

  function handleRemove(index) {
    if (multiple) {
      setFileOrFiles(prev => prev.filter((_, i) => i !== index))
    }
    else {
      setFileOrFiles(null)
    }
  }

  return (
    <div className={cn('flex flex-col gap-2.5 w-full', className)}>
      <FileUploader
        multiple={multiple}
        fileOrFiles={fileOrFiles}
        handleChange={handleAdd}
        name={name}
        types={types}
        {...props}
      >
        <div className="flex flex-col gap-1 w-full p-7 items-center justify-center rounded-sm border-dashed border-2 border-gray-200 hover:bg-gray-100 transition-colors cursor-pointer">
          {isAudioOnly
            ? (
                <PiFileAudio size={40} className="text-gray-400" />
              )
            : isCsvOnly
              ? (
                  <PiFileCsv size={30} className="text-gray-400" />
                )
              : (
                  <div className="flex items-center justify-center gap-0.5">
                    <PiFilePdf size={24} className="text-gray-400 rotate-6" />
                    <PiFileDoc size={30} className="text-gray-400 -rotate-6" />
                    <PiFileText size={36} className="text-gray-400" />
                    <PiFileAudio size={30} className="text-gray-400 -rotate-6" />
                    <PiFileVideo size={24} className="text-gray-400 rotate-6" />
                  </div>
                )}

          <h3 className="text-sm font-medium text-gray-700">{label ?? 'Upload Files'}</h3>

          {description && (
            <p className="text-xs text-gray-500">{description}</p>
          )}

          <p className="text-gray-500 text-xs">
            Drag and drop
            {' '}
            {multiple ? 'files' : 'a file'}
            {' '}
            here or
            {' '}
            <span className="underline cursor-pointer">click</span>
            {' '}
            to upload
          </p>

          {types && types.length > 0 && (
            <p className="text-gray-500 text-xs">
              Supported formats:
              {' '}
              {getReadableFormats()}
            </p>
          )}

        </div>
      </FileUploader>

      {multiple && fileOrFiles.length > 0 && (
        <ScrollShadow className="flex flex-col gap-1.5 w-full max-h-48 overflow-y-auto">
          {fileOrFiles.map((file, index) => (
            <FileCard key={index} file={file} handleRemove={() => handleRemove(index)} />
          ))}
        </ScrollShadow>
      )}
      {!multiple && fileOrFiles && (
        <FileCard file={fileOrFiles} handleRemove={() => handleRemove(0)} />
      )}
    </div>
  )
}
